// API服务层 - 统一管理所有API调用
import {
    getApiUrl,
    isCurrentApiHostPrimary,
    reportApiConnectionFailure,
    rewriteUrlToFallbackHost,
    PRIMARY_PROBE_TIMEOUT_MS
} from '../config/server-config.js';
import { getInitLanguageFields } from '../i18n';

class ApiService {
    constructor() {
        // 请求超时时间（业务请求，主备已定的情况下用这个）
        this.timeout = 30000;

        // 请求重试次数
        this.maxRetries = 3;

        // JWT token（API init 返回后设置）
        this._token = null;
    }

    // 设置 JWT token
    setToken(token) {
        this._token = token;
    }

    // 获取 JWT token
    getToken() {
        return this._token;
    }

    // API 基础 URL——动态取，不在构造时快照。主备切换发生在运行期（sessionStorage
    // 标记），必须每次都重新读，不然切换后已经拼死的 baseURL 不会跟着变。
    get baseURL() {
        return getApiUrl();
    }

    // 通用请求方法。isFailoverRetry：内部标记，主域名连接层失败后的原地重试用，
    // 调用方不用传。
    async request(url, options = {}, isFailoverRetry = false) {
        // 还没决出主备胜负、且这次确实打的是主域名时，用短探测超时（被墙用户
        // 应该很快摔到 fallback，而不是陪它等 30s 业务超时）。
        const onPrimary = !isFailoverRetry && isCurrentApiHostPrimary();
        const timeoutMs = onPrimary ? PRIMARY_PROBE_TIMEOUT_MS : this.timeout;
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

        const headers = {
            'Content-Type': 'application/json',
            ...options.headers
        };

        // 自动附加 JWT Authorization header
        if (this._token) {
            headers['Authorization'] = `Bearer ${this._token}`;
        }

        const defaultOptions = {
            // 强制绕过浏览器和 Service Worker 缓存：
            //   宿主页可能注册了 PWA SW（如 Angular ngsw-worker）拦截所有 fetch；
            //   `cache: 'no-store'` 让浏览器既不读 HTTP 缓存也不写，遵守的 SW 也会跳过这条请求
            //   不动 credentials，保留默认（same-origin），跨域请求由后端 CORS 头控制
            cache: 'no-store',
            headers,
            signal: controller.signal,
            ...options
        };

        try {
            const response = await fetch(url, defaultOptions);
            clearTimeout(timeoutId);

            if (!response.ok) {
                const httpError = new Error(`HTTP error! status: ${response.status}`);
                // 附上原始状态码，方便调用方精确分类（比如 widget.js 的错误页要区分
                // 服务端 5xx 和其它情况）而不用去解析上面这行的错误消息字符串。
                httpError.status = response.status;
                throw httpError;
            }

            const data = await response.json();
            return data;
        } catch (error) {
            clearTimeout(timeoutId);

            // 连接层失败：AbortError（我们自己的超时）或 fetch reject 产生的
            // TypeError（DNS/连接被拒/网络错误等）。业务 4xx/5xx 是 response.ok
            // 判断失败后我们自己 throw 的 Error，name 是 'Error' 不是这两种，
            // 不会走到这条分支——即"已连通"不触发切换。
            const isConnLayerFailure = error.name === 'AbortError' || error instanceof TypeError;
            if (isConnLayerFailure && onPrimary) {
                reportApiConnectionFailure();
                return this.request(rewriteUrlToFallbackHost(url), options, true);
            }

            if (error.name === 'AbortError') {
                throw new Error('Request timeout');
            }

            throw error;
        }
    }

    // 带重试的请求
    async requestWithRetry(url, options = {}, retries = 0) {
        try {
            return await this.request(url, options);
        } catch (error) {
            if (retries < this.maxRetries) {
                console.log(`Retrying request... (${retries + 1}/${this.maxRetries})`);
                await new Promise(resolve => setTimeout(resolve, 1000 * Math.pow(2, retries)));
                return this.requestWithRetry(url, options, retries + 1);
            }
            throw error;
        }
    }

    // ========== Chat API ==========

    // 初始化聊天（返回 JWT token）
    //
    // 身份双锚点：
    //   visitorUuid：前端 crypto.randomUUID() 生成的稳定标识，localStorage + cookie 双写
    //   fingerprint：浏览器指纹，供客服后台拉黑用
    // 后端必须两者同时命中才视为回访，任一不匹配 → 新访客（后端会返回权威 visitor_uuid）
    //
    // 兼容旧签名：若第 2 个参数是字符串且第 4 个参数是 null/undefined，退化到旧的 userId 语义
    // sdkIdentity: native-SDK pass-through 身份（WebView 打开 widget.html 时由
    // URL 参数注入）。可含 sdk_fp / sdk_device_id / external_user_id，服务端优先
    // 按它复用 /sdk/sessions 建的 client 行。
    async initChat(appId, visitorUuid, fingerprint, fromData = {}, legacyVisitorToken = null, sdkIdentity = null) {
        const url = `${this.baseURL}/chat`;
        const payload = { appId, fromData };
        // 只上报明确意图（访客手动选择 / 集成方显式配置），写 client.preferred_language。
        // 自动判定的显示语言不报：宿主页 <html lang> 和浏览器语言都不是访客的选择，
        // 服务端自己按 Accept-Language 判，判定规则见 i18n/lang-resolution.js。
        Object.assign(payload, getInitLanguageFields());
        if (visitorUuid)        payload.visitor_uuid = visitorUuid;
        if (fingerprint)        payload.fingerprint  = fingerprint;
        if (legacyVisitorToken) payload.visitor_token = legacyVisitorToken;
        if (sdkIdentity && typeof sdkIdentity === 'object') {
            if (sdkIdentity.sdk_fp)           payload.sdk_fp = sdkIdentity.sdk_fp;
            if (sdkIdentity.sdk_device_id)    payload.sdk_device_id = sdkIdentity.sdk_device_id;
            if (sdkIdentity.external_user_id) payload.external_user_id = sdkIdentity.external_user_id;
        }

        const result = await this.request(url, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
        const data = result?.data || result;
        if (data?.token) {
            this.setToken(data.token);
        }
        return result;
    }

    // 设置访客偏好语言(语言选择器)→ 后端 client.preferred_language。
    // 之后系统消息(转接/暂无客服等)+ AI 回复都会跟随该语言。
    // manual 来源优先级最高，后续 init 和首条消息校正都不会覆盖。
    async setLanguage(language) {
        const url = `${this.baseURL}/chat/set-language`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ language })
        });
    }

    // 发送消息 - 通过WebSocket发送，这里仅作备用
    async sendMessage(data) {
        // 消息主要通过WebSocket发送
        console.log('Message should be sent via WebSocket');
        return { success: true };
    }

    // 加载历史消息
    async loadMessages(chatId, visitorId, options = {}) {
        const params = new URLSearchParams({
            chat_id: chatId,
            limit: options.limit || 50,
            ...(options.beforeId && { before_id: options.beforeId })
        });

        const url = `${this.baseURL}/load-messages?${params}`;
        return await this.request(url);
    }

    // ========== Upload API ==========

    // 上传文件。kind 是可选的意图声明（目前只有 'voice' 一个值）——服务端靠
    // finfo 探测真实 MIME 决定落盘扩展名，但语音录制在浏览器里录出来的容器
    // (webm/opus) finfo 只能测出 video/webm（探测不出音轨/视轨），跟真正的
    // webm 视频/动画贴纸没法从 MIME 上区分。kind='voice' 是客户端明确的
    // "这是一条语音"意图，服务端据此把 video/webm|audio/webm 落成 .weba
    // （MessageTypeResolver 识别为 voice，不会撞进 .webm→animated_sticker 那条）。
    async uploadFile(file, appId, chatId, onProgress, kind) {
        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            const formData = new FormData();

            formData.append('file', file);
            formData.append('app_id', appId);
            if (chatId) {
                formData.append('chat_id', chatId);
            }
            if (kind) {
                formData.append('kind', kind);
            }

            xhr.upload.addEventListener('progress', (e) => {
                if (e.lengthComputable && onProgress) {
                    const percentComplete = (e.loaded / e.total) * 100;
                    onProgress(percentComplete);
                }
            });

            xhr.addEventListener('load', () => {
                if (xhr.status === 200) {
                    try {
                        const response = JSON.parse(xhr.responseText);
                        resolve(response);
                    } catch (error) {
                        reject(new Error('Invalid response format'));
                    }
                } else {
                    reject(new Error(`Upload failed with status: ${xhr.status}`));
                }
            });

            xhr.addEventListener('error', () => {
                reject(new Error('Upload failed'));
            });

            xhr.addEventListener('abort', () => {
                reject(new Error('Upload cancelled'));
            });

            xhr.open('POST', `${this.baseURL}/upload`, true);
            xhr.send(formData);
        });
    }

    // ========== Email & Binding API ==========

    // 生成绑定码
    async generateBinding(email, appId, chatId, visitorId) {
        const url = `${this.baseURL}/email/generate-binding`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                email,
                app_id: appId,
                chat_id: chatId,
                visitor_id: visitorId
            })
        });
    }

    // 验证邮箱
    async verifyEmail(code, email, appId, clientId) {
        const url = `${this.baseURL}/email/verify-code`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                code,
                email,
                app_id: appId,
                client_id: clientId
            })
        });
    }

    // 用已存邮箱一键确认绑定（跳过验证码）
    // 返回 { success, email } 或 { success:false, need_email:true }
    async confirmEmailBinding(appId, clientId) {
        const url = `${this.baseURL}/email/confirm-binding`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                app_id: appId,
                client_id: clientId
            })
        });
    }

    // 生成 Telegram 绑定深链（POST /v1/binding/telegram/generate）
    // 返回 { success, code, deep_link, bot_username }
    async generateTelegramBinding(appId, clientId) {
        const url = `${this.baseURL}/binding/telegram/generate`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                app_id: appId,
                client_id: clientId
            })
        });
    }

    // ========== Push Notification API ==========

    // 获取推送配置
    async getPushConfig(appId, visitorId) {
        const params = new URLSearchParams({
            app_id: appId,
            ...(visitorId && { fp: visitorId })
        });

        const url = `${this.baseURL}/push/config?${params}`;
        return await this.request(url);
    }

    // 订阅推送
    async subscribePush(subscription, appId, clientId, visitorId) {
        const url = `${this.baseURL}/push/subscribe`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                subscription,
                app_id: appId,
                client_id: clientId,
                fp: visitorId
            })
        });
    }

    // 取消订阅推送
    async unsubscribePush(endpoint, appId, clientId) {
        const url = `${this.baseURL}/push/unsubscribe`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                endpoint,
                app_id: appId,
                client_id: clientId
            })
        });
    }

    // 检查推送订阅状态
    async checkPushSubscription(appId, clientId) {
        const params = new URLSearchParams({ app_id: appId, client_id: clientId });
        const url = `${this.baseURL}/push/check?${params}`;
        return await this.request(url);
    }

    // 测试推送
    async testPush(subscription, appId) {
        const url = `${this.baseURL}/push/test`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                subscription,
                app_id: appId
            })
        });
    }

    // ========== Rating API ==========

    // 提交聊天评分
    async rateChat(chatId, rating, feedback = '') {
        const url = `${this.baseURL}/chat/rate`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ chat_id: chatId, rating, feedback })
        });
    }

    // ========== Forms API ==========

    // 提交富消息表单（spec 2026-04-17-rich-messages §9/§12.3）
    // payload: { app_id, chat_id, visitor_id, message_id, form_id, values }
    async submitForm(payload) {
        const url = `${this.baseURL}/forms/submit`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify(payload)
        });
    }

    // ========== Knowledge API ==========

    // 获取知识库列表
    async getKnowledgeList(appId) {
        const url = `${this.baseURL}/knowledge?app_id=${appId}`;
        return this.request(url);
    }

    // 搜索知识库
    async searchKnowledge(query, appId) {
        const url = `${this.baseURL}/knowledge/search?app_id=${appId}&search=${encodeURIComponent(query)}`;
        return this.request(url);
    }

    // ========== Usage Statistics API ==========

    // 记录使用开始
    async recordUsageStart(host, data = {}) {
        const url = `${this.baseURL}/usage/start?host=${host}`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify(data)
        });
    }

    // 记录聊天开始
    async recordChatStart(appId, chatId, visitorId) {
        const url = `${this.baseURL}/usage/chat-start`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                appId,
                chatId,
                visitorId
            })
        });
    }

    // 获取统计数据
    async getUsageStats(appId, dateFrom, dateTo) {
        const params = new URLSearchParams({
            app_id: appId,
            ...(dateFrom && { date_from: dateFrom }),
            ...(dateTo && { date_to: dateTo })
        });

        const url = `${this.baseURL}/usage/stats?${params}`;
        return await this.request(url);
    }

    // ========== Widget Chat List API ==========

    // 获取访客会话列表
    async getWidgetChats(appId, fp) {
        const params = new URLSearchParams({ app_id: appId, fp });
        const url = `${this.baseURL}/widget/chats?${params}`;
        return await this.request(url);
    }

    // 访客关闭会话
    async widgetCloseChat(appId, fp, chatId) {
        const url = `${this.baseURL}/widget/close`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ app_id: appId, fp, chat_id: chatId })
        });
    }

    // 创建新会话
    async widgetCreateChat(appId, fp) {
        const url = `${this.baseURL}/widget/create-chat`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({ app_id: appId, fp })
        });
    }

    // ========== Cloudflare Realtime API ==========

    // 生成 Realtime authToken
    async generateRealtimeToken(chatId, clientId, appId) {
        const url = `${this.baseURL}/realtime/generate-token`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                chat_id: chatId,
                client_id: clientId,
                app_id: appId
            })
        });
    }

    // 结束 Realtime 会话
    async endRealtimeSession(chatId, sessionId) {
        const url = `${this.baseURL}/realtime/end-session`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                chat_id: chatId,
                session_id: sessionId
            })
        });
    }

    // 获取 Realtime 会话状态
    async getRealtimeSessionStatus(sessionId) {
        const url = `${this.baseURL}/realtime/session-status?session_id=${sessionId}`;
        return await this.request(url);
    }

    // 通知对方开始通话
    async notifyCallStart(chatId, clientId, callType) {
        const url = `${this.baseURL}/realtime/notify-call`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                chat_id: chatId,
                client_id: clientId,
                call_type: callType, // 'audio' or 'video'
                action: 'start'
            })
        });
    }

    // 通知对方结束通话
    async notifyCallEnd(chatId, clientId) {
        const url = `${this.baseURL}/realtime/notify-call`;
        return await this.request(url, {
            method: 'POST',
            body: JSON.stringify({
                chat_id: chatId,
                client_id: clientId,
                action: 'end'
            })
        });
    }
}

// 导出单例
export default new ApiService();